import {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback
} from 'react'
import { useForm, Controller, FormProvider } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Stack,
  Typography,
  useTheme
} from '@mui/material'
import { Dialog } from 'frame/components'
import MuiTextfield from 'frame/components/mui-textfield'
import ServiceTile from './ServiceTile'
import StyledModalV2, {
  StyledModalV2Actions
} from 'frame/components/styled-modal-v2'
import { useStyles } from './RegisterInterestForm.styles'

// ----------------------------
// Schema Validation
// ----------------------------
const schema = z.object({
  mobile: z
    .string()
    .regex(/^\+?[0-9]{8,15}$/, 'Invalid mobile number')
    .nonempty('Mobile number is required'),
  comments: z.string().optional(),
  services: z.array(z.string()).min(1, 'Select at least one service'),
})

const RegisterInterestForm = ({
  open = true,
  values,
  setValues,
  onClose,
  onSave,
  servicesCatalog,
}) => {
  const classes = useStyles()
  const theme = useTheme()
  const [openUnsavedChangesModal, setOpenUnsavedChangesModal] = useState(false)

  const methods = useForm({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: {
      name: values?.name || '',
      email: values?.email || '',
      mobile: values?.mobile || '',
      comments: values?.comments || '',
      services: Array.isArray(values?.services) ? values.services : [],
    },
  })

  const { control, handleSubmit, watch, formState, setValue, reset } = methods
  const currentValues = watch()

  // ----------------------------
  // Track initial values to detect unsaved changes
  // ----------------------------
  const initialValuesRef = useRef(currentValues)
  useEffect(() => {
    if (open) {
      initialValuesRef.current = currentValues
    }
  }, [open])

  const isDirty = useMemo(() => {
    const orig = initialValuesRef.current
    return (
      orig.mobile !== currentValues.mobile ||
      orig.comments !== currentValues.comments ||
      orig.services.length !== currentValues.services.length ||
      currentValues.services.some((s) => !orig.services.includes(s))
    )
  }, [currentValues])

  // ----------------------------
  // Handle Service Selection
  // ----------------------------
  const toggleService = (id) => {
    const selected = currentValues.services.includes(id)
      ? currentValues.services.filter((x) => x !== id)
      : [...currentValues.services, id]
    setValue('services', selected, { shouldValidate: true })
    setValues((prev) => ({ ...prev, services: selected }))
  }

  // ----------------------------
  // Handlers
  // ----------------------------
  const handleRequestClose = useCallback(() => {
    if (isDirty) {
      setOpenUnsavedChangesModal(true)
    }
    else {onClose()}
  }, [isDirty, onClose])

  const handleUnsavedConfirm = useCallback(() => {
    setOpenUnsavedChangesModal(false)
    reset()
    onClose()
  }, [onClose, reset])

  const onSubmit = (data) => {
    onSave({
      name: values.name.trim(),
      email: values.email.trim(),
      mobile: data.mobile.trim(),
      comments: data.comments?.trim() || null,
      services: data.services,
    })
    reset()
  }

  const canSubmit =
    currentValues.mobile &&
    currentValues.services.length > 0 &&
    formState.isValid

  // ----------------------------
  // Render
  // ----------------------------
  return (
    <FormProvider {...methods}>
      <Dialog
        open={open}
        autoSize
        size="lg"
        aria-labelledby="Register interest"
        breadcrumbs=""
        title="Register interest"
        onClose={handleRequestClose}>
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <Grid container alignItems="stretch">
            {/* Left column - Services */}
            <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
              <Card className={classes.leftCard}>
                <CardContent className={classes.cardContentGrow}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 700, mb: 2 }}>
                    Services
                  </Typography>
                  <Box className={classes.borderPanel} sx={{ p: 2, flex: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{ color: 'text.secondary', mb: 2 }}>
                      Please select the service(s) of interest{' '}
                      <Box
                        component="span"
                        sx={{ color: theme.palette.error.main }}>
                        *
                      </Box>
                    </Typography>
                    <Stack spacing={1.25}>
                      {servicesCatalog.map((s) => (
                        <ServiceTile
                          key={s.id}
                          imageSrc={s.imageSrc}
                          centerImageSrc={s.logoSrc}
                          checked={currentValues.services.includes(s.id)}
                          onChange={() => toggleService(s.id)}/>
                      ))}
                    </Stack>
                    {formState.errors.services?.message && (
                      <Typography color="error" fontSize={12} mt={1}>
                        {formState.errors.services.message}
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Right column - Details */}
            <Grid item xs={12} md={8} sx={{ display: 'flex' }}>
              <Card className={classes.rightCard}>
                <CardContent className={classes.cardContentGrow}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 700, mb: 2 }}>
                    Your details
                  </Typography>
                  <Box
                    className={classes.borderPanel}
                    sx={{
                      p: 2.5,
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                    }}>
                    {/* Read-only fields */}
                    <Grid container spacing={2} sx={{ mb: 1,p:2 }}>
                      <Grid item xs={12} md={6}>
                        <Typography className={classes.labelXs}>
                          Name
                        </Typography>
                        <Typography className={classes.valueXs}>
                          {values?.name || '-'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography className={classes.labelXs}>
                          Email
                        </Typography>
                        <Typography className={classes.valueXs}>
                          {values?.email || '-'}
                        </Typography>
                      </Grid>
                    </Grid>

                    {/* Editable fields */}
                    <Grid container spacing={2} sx={{ flex: 1,pl:2 }}>
                      <Grid item xs={12} md={6} sx={{ width: '279px' }}>
                        <Controller
                          name="mobile"
                          control={control}
                          render={({ field }) => (
                            <MuiTextfield
                              {...field}
                              label="Mobile *"
                              error={Boolean(formState.errors?.mobile?.message)}
                              value={currentValues.mobile}
                              onChange={(e) => {
                                const newMobile = e.target.value
                                field.onChange(newMobile)
                                setValues((prev) => ({
                                  ...prev,
                                  mobile: newMobile,
                                }))
                              }}
                              sx={{ width: '80%' }}/>
                          )}/>
                        <Typography color="error" fontSize={12}>
                          {formState.errors?.mobile?.message}
                        </Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <Controller
                          name="comments"
                          control={control}
                          render={({ field }) => (
                            <MuiTextfield
                              {...field}
                              label="Any additional comments"
                              multiline
                              rows={4}
                              sx={{ width: '100%' }}
                              value={currentValues.comments}
                              onChange={(e) => {
                                const newComment = e.target.value
                                field.onChange(newComment)
                                setValues((prev) => ({
                                  ...prev,
                                  comments: newComment,
                                }))
                              }}/>
                          )}/>
                      </Grid>
                    </Grid>

                    {/* Submit */}
                    <Stack direction="row" justifyContent="flex-end" mt={2}>
                      <Button
                        type="submit"
                        variant="contained"
                        className={classes.applyBtn}
                        disabled={!canSubmit}>
                        Apply now
                      </Button>
                    </Stack>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </form>
      </Dialog>

      {/* Unsaved changes modal */}
      <StyledModalV2
        onClose={() => setOpenUnsavedChangesModal(false)}
        open={openUnsavedChangesModal}
        content={
          <Typography variant="h4" textAlign="center">
            Any changes will not be saved. Are you sure you want to continue?
          </Typography>
        }
        actions={
          <StyledModalV2Actions
            onCancel={() => setOpenUnsavedChangesModal(false)}
            onConfirm={handleUnsavedConfirm}/>
        }/>
    </FormProvider>
  )
}

export default RegisterInterestForm
